import {
  Component,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  BusinessSetting,
  PaginatedResult
} from 'app/models/business-setting';

import { BusinessSettingService } from 'app/services/business-setting/BusinessSettingService';

import { ImageItem } from 'app/services/gallery/gallery.service';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';


@Component({
  selector: 'app-business-setting',
  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    GalleryPickerComponent,
    ImageUrlPipe,
    ImageFallbackDirective
  ],

  templateUrl: './business-setting.component.html',
  styleUrl: './business-setting.component.scss',

  changeDetection: ChangeDetectionStrategy.Default
})
export class BusinessSettingComponent implements OnInit {

  // =========================================================
  // ALLOWED TYPES
  // =========================================================

  readonly allowedTypes = [
    'header_logo',
    'footer_logo',
    'system_logo_white',
    'system_logo_black'
  ];

  // =========================================================
  // DATA
  // =========================================================

  settings: BusinessSetting[] = [];

  // ✅ snake_case
  paged: PaginatedResult<BusinessSetting> = {
    items: [],
    page_number: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
    has_previous_page: false,
    has_next_page: false
  };

  // =========================================================
  // STATE
  // =========================================================

  loading = false;
  saving = false;
  deleting = false;

  searchTerm = '';

  pageNumber = 1;
  pageSize = 10;

  // =========================================================
  // MODALS
  // =========================================================

  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;

  selectedSetting: BusinessSetting | null = null;

  // =========================================================
  // GALLERY
  // =========================================================

  isGalleryModalOpen = false;
  selectedPhoto: ImageItem | null = null;

  // =========================================================
  // FORM
  // =========================================================

  form = {
    type: '',
    value: '',
    status: 1
  };

  // =========================================================
  // CONSTRUCTOR
  // =========================================================

  constructor(
    private businessSettingService: BusinessSettingService
  ) {}

  // =========================================================
  // INIT
  // =========================================================

  ngOnInit(): void {
    this.loadBusinessSettings();
  }

  // =========================================================
  // GET LIST
  // =========================================================

  loadBusinessSettings(): void {
    this.loading = true;

    this.businessSettingService
      .getBusinessSettings(this.pageNumber, this.pageSize)
      .subscribe({
        next: (response: any) => {

          // ✅ الـ API بيرجع snake_case
          const items = response?.items ?? [];

          const normalizedItems: BusinessSetting[] =
            items.map((item: any) => this.normalizeSetting(item));

          this.settings = normalizedItems;

          const totalCount = Number(response?.total_count ?? 0);
          const totalPages = Number(
            response?.total_pages ??
            Math.ceil(totalCount / this.pageSize)
          );

          this.paged = {
            items: normalizedItems,
            page_number: Number(response?.page_number ?? this.pageNumber),
            page_size: Number(response?.page_size ?? this.pageSize),
            total_count: totalCount,
            total_pages: totalPages,
            has_previous_page: Boolean(response?.has_previous_page ?? this.pageNumber > 1),
            has_next_page: Boolean(response?.has_next_page ?? this.pageNumber < totalPages)
          };

          this.pageNumber = this.paged.page_number;
          this.pageSize = this.paged.page_size;

          this.loading = false;
        },

        error: (error) => {
          console.error('Failed to load business settings:', error);

          this.settings = [];

          this.paged = {
            items: [],
            page_number: this.pageNumber,
            page_size: this.pageSize,
            total_count: 0,
            total_pages: 0,
            has_previous_page: false,
            has_next_page: false
          };

          this.loading = false;
        }
      });
  }

  // =========================================================
  // NORMALIZE
  // =========================================================

  private normalizeSetting(item: any): BusinessSetting {
    return {
      id: Number(item?.id ?? 0),
      type: item?.type ?? '',
      value: item?.value ?? '',
      status: Number(item?.status ?? 0)
    } as BusinessSetting;
  }

  // =========================================================
  // SEARCH
  // =========================================================

  get filteredSettings(): BusinessSetting[] {
    const search = this.normalize(this.searchTerm);

    if (!search) return this.settings;

    return this.settings.filter(setting =>
      this.normalize(setting.type).includes(search) ||
      this.normalize(setting.value ?? '').includes(search) ||
      this.normalize(this.getStatusLabel(setting.status)).includes(search)
    );
  }

  onSearch(): void {
    this.pageNumber = 1;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.pageNumber = 1;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').toLowerCase().trim();
  }

  // =========================================================
  // CREATE MODAL
  // =========================================================

  openCreateModal(): void {
    this.form = { type: '', value: '', status: 1 };
    this.selectedSetting = null;
    this.selectedPhoto = null;
    this.isGalleryModalOpen = false;
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    if (this.saving) return;
    this.closeGalleryModal();
    this.showCreateModal = false;
    this.selectedPhoto = null;
  }

  // =========================================================
  // CREATE
  // =========================================================

  create(): void {
    if (!this.isFormValid()) return;

    this.saving = true;

    const payload = {
      type: this.form.type.trim(),
      value: this.form.value.trim(),
      status: Number(this.form.status)
    };

    this.businessSettingService
      .createBusinessSetting(payload as any)
      .subscribe({
        next: () => {
          this.saving = false;
          this.showCreateModal = false;
          this.selectedSetting = null;
          this.selectedPhoto = null;
          this.pageNumber = 1;
          this.loadBusinessSettings();
        },
        error: (error) => {
          console.error('Failed to create business setting:', error);
          this.saving = false;
        }
      });
  }

  // =========================================================
  // EDIT MODAL
  // =========================================================

  openEditModal(setting: BusinessSetting): void {
    this.selectedSetting = setting;

    this.form = {
      type: setting.type ?? '',
      value: setting.value ?? '',
      status: Number(setting.status ?? 0)
    };

    this.selectedPhoto = null;
    this.isGalleryModalOpen = false;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    if (this.saving) return;
    this.closeGalleryModal();
    this.showEditModal = false;
    this.selectedSetting = null;
    this.selectedPhoto = null;
  }

  // =========================================================
  // UPDATE
  // =========================================================

  update(): void {
    if (!this.selectedSetting || !this.isFormValid()) return;

    this.saving = true;

    const payload = {
      type: this.form.type.trim(),
      value: this.form.value.trim(),
      status: Number(this.form.status)
    };

    this.businessSettingService
      .updateBusinessSetting(this.selectedSetting.id, payload as any)
      .subscribe({
        next: () => {
          this.saving = false;
          this.showEditModal = false;
          this.selectedSetting = null;
          this.selectedPhoto = null;
          this.loadBusinessSettings();
        },
        error: (error) => {
          console.error('Failed to update business setting:', error);
          this.saving = false;
        }
      });
  }

  // =========================================================
  // DELETE MODAL
  // =========================================================

  openDeleteModal(setting: BusinessSetting): void {
    this.selectedSetting = setting;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    if (this.deleting) return;
    this.showDeleteModal = false;
    this.selectedSetting = null;
  }

  delete(): void {
    if (!this.selectedSetting) return;

    this.deleting = true;

    this.businessSettingService
      .deleteBusinessSetting(this.selectedSetting.id)
      .subscribe({
        next: () => {
          this.deleting = false;
          this.showDeleteModal = false;
          this.selectedSetting = null;

          if (this.settings.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }

          this.loadBusinessSettings();
        },
        error: (error) => {
          console.error('Failed to delete business setting:', error);
          this.deleting = false;
        }
      });
  }

  // =========================================================
  // FORM VALIDATION
  // =========================================================

  isFormValid(): boolean {
    const type = this.form.type?.trim();
    const value = this.form.value?.trim();

    return !!(
      type &&
      value &&
      (this.form.status === 0 || this.form.status === 1)
    );
  }

  // =========================================================
  // CHECK TYPE
  // =========================================================

  isAllowedType(type: string | null | undefined): boolean {
    if (!type) return false;
    return this.allowedTypes.includes(type);
  }

  // =========================================================
  // TYPE LABEL
  // =========================================================

  getTypeLabel(type: string | null | undefined): string {
    switch (type) {
      case 'header_logo': return 'Header Logo';
      case 'footer_logo': return 'Footer Logo';
      case 'system_logo_white': return 'System Logo White';
      case 'system_logo_black': return 'System Logo Black';
      default: return type || '—';
    }
  }

  // =========================================================
  // STATUS LABEL
  // =========================================================

  getStatusLabel(status: number | string | null | undefined): string {
    return Number(status) === 1 ? 'Active' : 'Inactive';
  }

  isActive(status: number | string | null | undefined): boolean {
    return Number(status) === 1;
  }

  // =========================================================
  // IMAGE URL
  // =========================================================

  isImageUrl(value: string | null | undefined): boolean {
    if (!value) return false;
    return (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('/')
    );
  }

  // =========================================================
  // GALLERY
  // =========================================================

  openGalleryModal(): void {
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    if (!images || images.length === 0) return;

    const image = images[images.length - 1];

    this.selectedPhoto = image;
    this.form.value = this.getFullImageUrl(image);

    this.closeGalleryModal();
  }

  private getFullImageUrl(image: ImageItem): string {
    if (!image) return '';
    const url = (image as any).url ?? (image as any).Url ?? '';
    return String(url).trim();
  }

  removeSelectedPhoto(): void {
    this.selectedPhoto = null;
    this.form.value = '';
  }

  // =========================================================
  // PAGINATION ✅ snake_case
  // =========================================================

  goToPage(page: number): void {
    if (
      this.loading ||
      page < 1 ||
      page > this.paged.total_pages ||
      page === this.pageNumber
    ) return;

    this.pageNumber = page;
    this.loadBusinessSettings();
  }

  previousPage(): void {
    if (this.loading || !this.paged.has_previous_page) return;
    this.pageNumber--;
    this.loadBusinessSettings();
  }

  nextPage(): void {
    if (this.loading || !this.paged.has_next_page) return;
    this.pageNumber++;
    this.loadBusinessSettings();
  }

  onPageSizeChange(): void {
    this.pageNumber = 1;
    this.loadBusinessSettings();
  }

  // =========================================================
  // PAGE NUMBERS
  // =========================================================

  get pageNumbers(): number[] {
    const total = Number(this.paged.total_pages || 0);
    const current = Number(this.pageNumber || 1);

    if (total <= 0) return [];

    const maxPages = 5;

    let start = Math.max(1, current - Math.floor(maxPages / 2));
    let end = Math.min(total, start + maxPages - 1);

    if (end - start + 1 < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }

    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  // =========================================================
  // TRACK BY
  // =========================================================

  trackById(index: number, item: BusinessSetting): number {
    return Number(item.id);
  }

  // =========================================================
  // SHOWING FROM / TO
  // =========================================================

  get showingFrom(): number {
    if (this.paged.total_count === 0) return 0;
    return ((this.pageNumber - 1) * this.pageSize) + 1;
  }

  get showingTo(): number {
    return Math.min(
      this.pageNumber * this.pageSize,
      this.paged.total_count
    );
  }
}